import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { merchantService, ApiError } from '../services';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const readonlyInputClass =
  'w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-400 bg-gray-50 cursor-not-allowed';

// E.164 sanity: starts with + then 8–15 digits (loose; BE validates strict).
const E164_PATTERN = /^\+\d{8,15}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Per release notes 4.4: BE resolves the owner account via company_users (role=owner)
// or company.account_id. We send name/phone/email; BE handles the routing.
// We split BE's single `name` string into first/last for form display.
const splitName = full => {
  const parts = String(full ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const formDataFromMerchant = m => {
  const { firstName, lastName } = splitName(m?.ownerFullName);
  return {
    firstName,
    lastName,
    phone: (m?.ownerPhone && m.ownerPhone !== 'N/A' ? m.ownerPhone : '') || '',
    email: (m?.ownerEmail && m.ownerEmail !== 'N/A' ? m.ownerEmail : '') || '',
  };
};

const MerchantEditBusinessOwnerPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const merchantFromState = location.state?.merchant;

  const [merchant, setMerchant] = useState(merchantFromState || null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const initialFetchDoneRef = useRef(false);
  const justVerifiedRef = useRef(false);

  const detailHref = id
    ? ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id })
    : ROUTES.MERCHANTS;

  const goBackToDetail = useCallback(() => {
    if (!id) {
      navigate(ROUTES.MERCHANTS);
      return;
    }
    navigate(detailHref, { state: merchant ? { merchant } : undefined });
  }, [detailHref, id, merchant, navigate]);

  const loadMerchant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await merchantService.getFormattedMerchantDetails(id);
      const data = response?.data;
      if (!data) {
        setError('Failed to load merchant details.');
        return;
      }
      setMerchant(data);
      const isRevealed = data.revealed === true;
      setRevealed(isRevealed);
      if (isRevealed) {
        setForm(formDataFromMerchant(data));
      } else {
        setForm(formDataFromMerchant({}));
        setRevealModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load merchant:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load merchant. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadMerchant();
  }, [loadMerchant]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (savedOk) setSavedOk(false);
  };

  const validate = currentForm => {
    const next = {};
    if (!String(currentForm.firstName ?? '').trim()) {
      next.firstName = 'First Name is required.';
    }
    if (!String(currentForm.lastName ?? '').trim()) {
      next.lastName = 'Last Name is required.';
    }
    const phone = String(currentForm.phone ?? '').trim();
    if (!phone) {
      next.phone = 'Phone Number is required.';
    } else if (!E164_PATTERN.test(phone)) {
      next.phone = 'Phone must be in E.164 format (e.g. +393331112222).';
    }
    const email = String(currentForm.email ?? '').trim();
    if (!email) {
      next.email = 'Email Address is required.';
    } else if (!EMAIL_PATTERN.test(email)) {
      next.email = 'Email format is invalid.';
    }
    return next;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      // BE expects full update: always send name + phone + email together.
      // Concat first + last into single `name` field per BE contract (4.1).
      const name = [String(form.firstName).trim(), String(form.lastName).trim()]
        .filter(Boolean)
        .join(' ');
      await merchantService.updateBusinessOwner(id, {
        name,
        phone: String(form.phone).trim(),
        email: String(form.email).trim(),
        // Preserve verified flag — consistent with personal-info flow.
        skipVerificationReset: true,
      });
      setSavedOk(true);
      // Mirror saved values into merchant cache so detail page reflects them
      // when admin clicks Back to Details.
      setMerchant(prev =>
        prev
          ? {
              ...prev,
              ownerFullName: name,
              ownerPhone: String(form.phone).trim(),
              ownerEmail: String(form.email).trim(),
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to save business owner:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'PhoneAlreadyUsed') {
        setFieldErrors(prev => ({
          ...prev,
          phone: 'Phone number is already used by another account.',
        }));
        setError('Phone number is already used by another account.');
      } else if (code === 'EmailAlreadyUsed') {
        setFieldErrors(prev => ({
          ...prev,
          email: 'Email is already used by another account.',
        }));
        setError('Email is already used by another account.');
      } else if (code === 'FeatureInvalidPayload') {
        setError(
          err.message ||
            'Validation failed. Please review name, phone, and email.'
        );
      } else if (code === 'FeatureUnauthorized') {
        setError(
          'Your admin session has expired or lacks permission. Please re-verify and try again.'
        );
      } else if (code === 'DatabaseNotFound') {
        setError('Merchant or business owner account not found.');
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to save business owner. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const merchantLabel = merchant?.companyName || 'Merchant';

  if (loading && !merchant) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  const editable = revealed && !saving;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Edit Business Owner'
        subtitle='Edit the business owner information and make sure the data is correct'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Merchants', href: ROUTES.MERCHANTS },
          { name: merchantLabel, href: detailHref, state: { merchant } },
          { name: 'Edit Business Owner' },
        ]}
      />

      {savedOk && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>Business owner saved.</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white rounded-lg shadow-sm border border-gray-200'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {merchantLabel}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Merchant ID: {merchant?.id || '-'}
          </p>
        </div>

        <div className='p-6 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
            <div>
              <label
                htmlFor='firstName'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                First Name <span className='text-red-600'>*</span>
              </label>
              <input
                id='firstName'
                type='text'
                value={form?.firstName ?? ''}
                onChange={handleChange('firstName')}
                disabled={!editable}
                className={inputClassFor('firstName')}
                autoComplete='given-name'
                aria-invalid={hasFieldError('firstName')}
              />
              {hasFieldError('firstName') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.firstName}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='lastName'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Last Name <span className='text-red-600'>*</span>
              </label>
              <input
                id='lastName'
                type='text'
                value={form?.lastName ?? ''}
                onChange={handleChange('lastName')}
                disabled={!editable}
                className={inputClassFor('lastName')}
                autoComplete='family-name'
                aria-invalid={hasFieldError('lastName')}
              />
              {hasFieldError('lastName') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.lastName}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='phone'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Phone Number <span className='text-red-600'>*</span>
              </label>
              <input
                id='phone'
                type='tel'
                value={form?.phone ?? ''}
                onChange={handleChange('phone')}
                disabled={!editable}
                className={inputClassFor('phone')}
                autoComplete='tel'
                placeholder='+393331112222'
                aria-invalid={hasFieldError('phone')}
              />
              {hasFieldError('phone') && (
                <p className='mt-1 text-xs text-red-600'>{fieldErrors.phone}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Email Address <span className='text-red-600'>*</span>
              </label>
              <input
                id='email'
                type='email'
                value={form?.email ?? ''}
                onChange={handleChange('email')}
                disabled={!editable}
                className={inputClassFor('email')}
                autoComplete='email'
                aria-invalid={hasFieldError('email')}
              />
              {hasFieldError('email') && (
                <p className='mt-1 text-xs text-red-600'>{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='accountId'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Account ID
              </label>
              <input
                id='accountId'
                type='text'
                value={merchant?.accountId || merchant?.ownerAccountId || ''}
                readOnly
                disabled
                className={readonlyInputClass}
              />
            </div>
            <div>
              <label
                htmlFor='joinDate'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Join Date
              </label>
              <input
                id='joinDate'
                type='text'
                value={merchant?.ownerJoinDate || ''}
                readOnly
                disabled
                className={readonlyInputClass}
              />
            </div>
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={goBackToDetail}
              disabled={saving}
              className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!editable}
              className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Business Owner Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the business owner. Under GDPR, you
              are responsible for ensuring that the data is accurate and up to
              date. This action will be logged with your user ID and timestamp
              for audit purposes.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          await loadMerchant();
        }}
      />
    </div>
  );
};

export default MerchantEditBusinessOwnerPage;
